import {
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react"
import {
  uniqBy,
  find,
  findIndex,
  reject,
  debounce,
  orderBy,
} from 'lodash'

import DatabaseContext from '../../../../src/contexts/Database/DatabaseContext'
import CLIENT_INFOS from '../BankReconciliation.constants/CLIENT_INFOS'

import ITEMS_PER_PAGE from "../BankReconciliation.constants/ITEMS_PER_PAGE"
import DEFAULT_AMOUNT_FILTER from "../BankReconciliation.constants/DEFAULT_AMOUNT_FILTER"

import updateStyles from "../BankReconciliation.functions/updateStyles.js"
import filterBankTransfers from "../BankReconciliation.functions/filterBankTransfers"
import calculateSummary from "../BankReconciliation.functions/calculateSummary"
// import parseDate from '../BankReconciliation.functions/parseDate'
import handleDateFilterChange from "../BankReconciliation.functions/handleDateFilterChange"
import filterInvoices from "../BankReconciliation.functions/filterInvoices"

import useBackendActions from './useBackendActions'

const useBankReconciliationPage = () => {
  const {
    selectedBankTransfer,
    matchedInvoices,
    onSelectedBankTransferChange,
    bankTransfers = [],
    invoices: allInvoices = [],
    transfersOrdersMatches = [],
  } = useContext(DatabaseContext)

  // 🔍 DIAGNOSTIC LOGS - Données reçues du contexte Database
  console.log('🔍 useBankReconciliationPage - Data Analysis:')
  console.log('  📊 allInvoices count:', Array.isArray(allInvoices) ? allInvoices.length : 'NOT_ARRAY')
  console.log('  🏦 bankTransfers count:', Array.isArray(bankTransfers) ? bankTransfers.length : 'NOT_ARRAY')
  console.log('  🔗 transfersOrdersMatches count:', Array.isArray(transfersOrdersMatches) ? transfersOrdersMatches.length : 'NOT_ARRAY')
  console.log('  📄 matchedInvoices count:', Array.isArray(matchedInvoices) ? matchedInvoices.length : 'NOT_ARRAY')

  if (Array.isArray(transfersOrdersMatches) && transfersOrdersMatches.length > 0) {
    console.log('  🔗 transfersOrdersMatches sample:', transfersOrdersMatches.slice(0, 3))
  }

  // All states declarations first
  const [isSidebarOpen, setsidebarOpen] = useState(false)
  const [transactionSearchTerm, settransactionSearchTerm] = useState("")
  const [filterStatus, setfilterStatus] = useState("all")
  const [sortedBankTransfers, setsortedBankTransfers] = useState([])
  const [currentPage, setcurrentPage] = useState(1)
  const [currentPageBankTransfers, setcurrentPageBankTransfers] = useState([])
  const [bankTransfersAmountFilter, setbankTransfersAmountFilter] = useState(DEFAULT_AMOUNT_FILTER)
  const [invoicesAmountFilter, setinvoicesAmountFilter] = useState(DEFAULT_AMOUNT_FILTER)
  const [bankTransfersDateFilter, setbankTransfersDateFilter] = useState(null)
  const [invoicesDateFilter, setinvoicesDateFilter] = useState(null)
  const [itemsPerPage, setitemsPerPage] = useState(ITEMS_PER_PAGE)
  const [commentErrorMessage, setcommentErrorMessage] = useState('')
  const [isReconciledSuccessMessageVisible, setisReconciledSuccessMessageVisible] = useState(false)
  const [onInvoiceUnmatchPressErrorMessage, setonInvoiceUnmatchPressErrorMessage] = useState('')
  const [isCommentFilterActive, setisCommentFilterActive] = useState(false)
  const [plusFilterShowMore, setplusFilterShowMore] = useState(false)
  const [isVerificationSelectorFilterOpen, setisVerificationSelectorFilterOpen] = useState(false)
  const [verificationSelectorSelectedFilter, setverificationSelectorSelectedFilter] = useState("")
  const [isInvoicesPopupModalOpen, setisInvoicesPopupModalOpen] = useState(false)
  const [isInvoicesPopupMatchesValidatedSideMessageVisible, setisInvoicesPopupMatchesValidatedSideMessageVisible] = useState(false)
  const [showInvoicesPopupMessageDeleted, setShowInvoicesPopupMessageDeleted] = useState(false)
  const [invoicesPopupFilteredInvoices, setinvoicesPopupFilteredInvoices] = useState([])
  const [selectedInvoices, setSelectedInvoices] = useState([])
  const [onMatchSelectedInvoicesPressErrorMessage, setonMatchSelectedInvoicesPressErrorMessage] = useState('')
  const [isUnlinkedInvoicesModalOpen, setIsUnlinkedInvoicesModalOpen] = useState(false)
  const [unlinkedInvoices, setUnlinkedInvoices] = useState([])
  const [unlinkedInvoicesSearchTerm, setUnlinkedInvoicesSearchTerm] = useState("")
  const [unlinkedInvoicesSortConfig, setUnlinkedInvoicesSortConfig] = useState({
    key: null,
    direction: 'asc',
  })

  // Smart Invoice Selector states
  const [smartInvoiceSelectorPrompt, setSmartInvoiceSelectorPrompt] = useState('')
  const [smartInvoiceSelectorFiles, setSmartInvoiceSelectorFiles] = useState([])
  const [isSmartInvoiceSelectorProcessing, setIsSmartInvoiceSelectorProcessing] = useState(false)
  const [smartInvoiceSelectorError, setSmartInvoiceSelectorError] = useState('')

  // Then initialize backendActionsLogic with all the setters
  const backendActionsLogic = useBackendActions({
    setonInvoiceUnmatchPressErrorMessage,
    setisReconciledSuccessMessageVisible,
    setcommentErrorMessage,
    setonMatchSelectedInvoicesPressErrorMessage,
    setisInvoicesPopupModalOpen,
    setisInvoicesPopupMatchesValidatedSideMessageVisible,
    setSmartInvoiceSelectorError,
  })

  const sideBarReconcileButtonColor = selectedBankTransfer?.isBankTransferReconciled ? 'green' : 'grey'
  const sideBarReconcileButtonText = selectedBankTransfer?.isBankTransferReconciled ? 'Reconciled' : 'Reconcile ⚡'

  const totalPages = Math.ceil(sortedBankTransfers.length / itemsPerPage)

  const {
    onSmartInvoicesSelectionPress,
  } = backendActionsLogic

  // Créer un Set des enterpriseCodes valides pour une recherche plus rapide
  const validEnterpriseCodes = useMemo(
    () => new Set(CLIENT_INFOS.map(client => client.enterpriseCode)),
    [],
  )

  const onUnlinkedInvoicesSort = (key) => {
    const direction = unlinkedInvoicesSortConfig.key === key && unlinkedInvoicesSortConfig.direction === 'asc'
      ? 'desc'
      : 'asc'
    setUnlinkedInvoicesSortConfig({ key, direction })
  }

  const onUnlinkedInvoicesSearchPress = debounce((searchTerm) => {
    setUnlinkedInvoicesSearchTerm(searchTerm)
  }, 400)

  // Calculer les factures non liées quand la modal s'ouvre ou quand la recherche change
  useEffect(() => {
    if (isUnlinkedInvoicesModalOpen) {
      // Filtrer les factures qui n'ont pas leur orderNumber dans la table de jointure
      const newUnlinkedInvoices = (Array.isArray(allInvoices) ? allInvoices : [])
        .filter(invoice => {
          const { orderNumber = '' } = invoice || {}
          const isLinked = (Array.isArray(transfersOrdersMatches) ? transfersOrdersMatches : []).some(match => {
            const { orderNumber: matchOrderNumber = '' } = match || {}
            return matchOrderNumber === orderNumber
          })
          return !isLinked
        })
        // Ne garder que les factures avec un enterpriseCode valide
        .filter(invoice => {
          const { enterpriseCode = '' } = invoice || {}
          return validEnterpriseCodes.has(enterpriseCode)
        })
        // Ne garder que les factures qui ne sont pas exemptées de preuve. Ex : avant 2024
        .filter((invoice = {}) => {
          const { isProofExempt = false } = invoice
          return !isProofExempt
        })
        // Ne garder que les factures avec un montant supérieur à 5
        .filter(invoice => {
          const { invoiceAmount = 0 } = invoice || {}
          return invoiceAmount > 5
        })
        // Filtrer par le terme de recherche
        .filter(invoice => {
          if (!unlinkedInvoicesSearchTerm) return true
          const { orderNumber = '', enterpriseCode = '' } = invoice || {}
          return orderNumber.toLowerCase().includes(unlinkedInvoicesSearchTerm.toLowerCase())
            || enterpriseCode.toLowerCase().includes(unlinkedInvoicesSearchTerm.toLowerCase())
        })

      setUnlinkedInvoices(newUnlinkedInvoices)
    }
  }, [isUnlinkedInvoicesModalOpen, allInvoices, transfersOrdersMatches, unlinkedInvoicesSearchTerm, validEnterpriseCodes])

  const sortedUnlinkedInvoices = useMemo(() => {
    const invoices = Array.isArray(unlinkedInvoices) ? unlinkedInvoices : []
    if (!unlinkedInvoicesSortConfig.key) return invoices

    return orderBy(
      invoices,
      [unlinkedInvoicesSortConfig.key],
      [unlinkedInvoicesSortConfig.direction],
    )
  }, [unlinkedInvoices, unlinkedInvoicesSortConfig])

  useEffect(() => {
    const asyncMethod = async () => {
      const {
        sortedBankTransfers: newsortedBankTransfers,
      } = await filterBankTransfers({
        bankTransfersAmountFilter,
        bankTransfers,
        bankTransfersDateFilter,
        filterStatus,
        transactionSearchTerm,
      })
      setsortedBankTransfers(newsortedBankTransfers)
      setcurrentPageBankTransfers(newsortedBankTransfers.slice(
        (currentPage - 1) * itemsPerPage,
        (currentPage) * itemsPerPage,
      ))
    }
    asyncMethod()
  }, [
    bankTransfers,
    // JSON.stringify(bankTransfers), // eslint-disable-line
    transactionSearchTerm,
    filterStatus,
    currentPage,
    bankTransfersDateFilter,
    bankTransfersAmountFilter,
    itemsPerPage,
  ])

  useEffect(() => {
    setcurrentPage(1) // reset the page when searching
  }, [transactionSearchTerm])

  useEffect(() => {
    onInvoiceSearchPressDebounced()
  }, [invoicesDateFilter, invoicesAmountFilter]) // eslint-disable-line

  // USELESS BELOW?? Tested, do not see the impact
  useEffect(() => {
    updateStyles()

    const handleResizeScroll = () => {
      updateStyles()
    }

    window.addEventListener("resize", handleResizeScroll)
    window.addEventListener("scroll", handleResizeScroll)

    return () => {
      window.removeEventListener("resize", handleResizeScroll)
      window.removeEventListener("scroll", handleResizeScroll)
    }
  }, [])

  // Pre-select already matched invoices
  useEffect(() => {
    setSelectedInvoices(Array.isArray(matchedInvoices) ? matchedInvoices : [])
  }, [JSON.stringify(matchedInvoices)]) // eslint-disable-line

  // REFRESH : Hide previous message when changing selected transfer focus
  useEffect(() => {
    setisInvoicesPopupMatchesValidatedSideMessageVisible(false)
    setcommentErrorMessage('')
    setisReconciledSuccessMessageVisible('')
    console.log('selectedBankTransfer', selectedBankTransfer)
  }, [selectedBankTransfer?.id])

  useEffect(() => {
    if (Array.isArray(allInvoices) && allInvoices.length) {
      setinvoicesPopupFilteredInvoices(uniqBy([
        // ...selectedInvoices,
        ...allInvoices,
      ], 'orderNumber'))
    }
  }, [
    allInvoices,
    // selectedInvoices,
    // selectedBankTransfer,
  ])

  const onItemsPerPageChange = (newItemsPerPage) => {
    setitemsPerPage(newItemsPerPage)
    setcurrentPage(1)
    onPageChange(1)
  }

  const onTransactionSearchPress = debounce(settransactionSearchTerm, 400)

  const onBankTransfersAmountFilterChange = ({
    value = "",
    minAmount = "",
    maxAmount = "",
  } = {}) => {
    setbankTransfersAmountFilter({ value, minAmount, maxAmount })
  }

  const onRowClick = (transaction) => {
    onSelectedBankTransferChange(transaction)
    setsidebarOpen(true)
  }
  const statusFilter = filterStatus
  const onBankTransfersDateFilterChange = (filter) => {
    if (!filter) {
      setbankTransfersDateFilter()
      return
    }
    const { start, end } = handleDateFilterChange(filter)
    setbankTransfersDateFilter({ start, end })
  }

  const onInvoiceSearchPress = (searchTerm) => {
    setinvoicesPopupFilteredInvoices(uniqBy([
      // ...selectedInvoices,
      ...(filterInvoices({
        allInvoices,
        searchTerm,
        invoicesDateFilter,
        invoicesAmountFilter,
      })),
    ], 'orderNumber'))
  }
  const onInvoiceSearchPressDebounced = debounce(onInvoiceSearchPress)

  const onInvoicesDateFilterChange = (filter) => {
    if (!filter) {
      setinvoicesDateFilter()
      return
    }
    const { start, end } = handleDateFilterChange(filter)
    setinvoicesDateFilter({ start, end })
  }

  const onInvoicesAmountFilterChange = ({
    value = "",
    minAmount = "",
    maxAmount = "",
  } = {}) => {
    setinvoicesAmountFilter({ value, minAmount, maxAmount })
  }
  const onSidebarClose = () => setsidebarOpen(false)
  const onSideBarMainScreenPress = onSidebarClose

  // PrimaryView
  const onPageChange = (activePage) => {
    setcurrentPage(activePage)
  }

  // FilterStatusButton
  const onTransactionTabPress = (filter) => {
    setTimeout(() => {
      setfilterStatus(filter)
    }, 100)
  }

  const onReconcileSuccessMessageClose = () => setisReconciledSuccessMessageVisible(false)

  const onCommentFilterButtonPress = () => {
    setisCommentFilterActive(!isCommentFilterActive)
  }
  const onCommentFilterFocusKeyPress = ({
    key = '',
  } = {}) => { if (key === "Enter" || key === " ") onCommentFilterButtonPress() }

  const onPlusFilterPlusClick = () => {
    setplusFilterShowMore(true)
  }

  const onPlusFilterLessClick = () => {
    setplusFilterShowMore(false)
  }

  const verificationSelectorToggleFilter = () => {
    setisVerificationSelectorFilterOpen(!isVerificationSelectorFilterOpen)
  }

  const onVerificationSelectorSelect = (filter) => {
    setverificationSelectorSelectedFilter(filter)
    setisVerificationSelectorFilterOpen(false)
  }

  const onVerificationSelectorClearFilter = () => {
    setverificationSelectorSelectedFilter("")
  }

  const onVerificationSelectorClose = () => setisVerificationSelectorFilterOpen(false)

  const onInvoiceCheckboxPress = (currentInvoiceRow) => {
    const { orderNumber = '' } = currentInvoiceRow || {}
    setSelectedInvoices(
      (oldSelectedInvoices) => (
        find(oldSelectedInvoices, currentInvoiceRow)
          ? reject(oldSelectedInvoices, { orderNumber })
          : [...oldSelectedInvoices, currentInvoiceRow]
      ),
    )
  }

  const getBankTransferRowBgColor = ({ id = '' } = {}) => {
    const {
      id: selectedId = '',
    } = selectedBankTransfer || {}

    return (id === selectedId)
      ? '#f5f5f5'
      : 'white'
  }

  const onSideBarNextPress = () => {
    const {
      id: currentSelectedId,
    } = selectedBankTransfer || {}
    const transfers = Array.isArray(currentPageBankTransfers) ? currentPageBankTransfers : []
    if (transfers.length > 0) {
      onSelectedBankTransferChange(transfers[
        (
          findIndex(transfers, { id: currentSelectedId }) + 1
        ) % transfers.length
      ])
    }
  }
  const onSideBarPrevPress = () => {
    const {
      id: currentSelectedId,
    } = selectedBankTransfer || {}
    const transfers = Array.isArray(currentPageBankTransfers) ? currentPageBankTransfers : []
    if (transfers.length > 0) {
      onSelectedBankTransferChange(transfers[
        (
          findIndex(transfers, { id: currentSelectedId }) - 1 + transfers.length
        ) % transfers.length
      ])
    }
  }
  const onSideBarPrevPagePress = () => {
    onPageChange(Math.max(1, (currentPage - 1)))
  }
  const onSideBarNextPagePress = () => {
    onPageChange(Math.min((currentPage + 1), totalPages))
  }

  const onUnlinkedInvoicesPress = () => setIsUnlinkedInvoicesModalOpen(true)
  const onUnlinkedInvoicesModalClose = () => setIsUnlinkedInvoicesModalOpen(false)

  const onSmartInvoiceSelectorPromptChange = (text) => {
    setSmartInvoiceSelectorPrompt(text)
    setSmartInvoiceSelectorError('')
  }

  const onSmartInvoiceSelectorFilesChange = (files) => {
    setSmartInvoiceSelectorFiles(files)
    setSmartInvoiceSelectorError('')
  }

  const onSmartInvoiceSelectorSubmit = async () => {
    if (!smartInvoiceSelectorPrompt && !smartInvoiceSelectorFiles.length) {
      setSmartInvoiceSelectorError('Veuillez saisir du texte ou ajouter des fichiers')
      return
    }

    setIsSmartInvoiceSelectorProcessing(true)

    const {
      orderNumbers = [],
    } = await onSmartInvoicesSelectionPress({
      prompt: smartInvoiceSelectorPrompt,
      files: smartInvoiceSelectorFiles,
    })

    const newInvoices = (Array.isArray(allInvoices) ? allInvoices : []).filter((invoice) => {
      const { orderNumber = '' } = invoice || {}
      return (Array.isArray(orderNumbers) ? orderNumbers : []).includes(orderNumber)
    })
    setSelectedInvoices((prev) => uniqBy([...prev, ...newInvoices], 'orderNumber'))
    setIsSmartInvoiceSelectorProcessing(false)
  }

  const [invoicesSortConfig, setInvoicesSortConfig] = useState({
    key: null,
    direction: 'asc',
  })

  const onInvoicesSort = (key) => {
    const direction = invoicesSortConfig.key === key && invoicesSortConfig.direction === 'asc'
      ? 'desc'
      : 'asc'
    setInvoicesSortConfig({ key, direction })
  }

  const sortedInvoices = useMemo(
    () => uniqBy(
      [
        ...(Array.isArray(selectedInvoices) ? selectedInvoices : []),
        ...orderBy(
          Array.isArray(invoicesPopupFilteredInvoices) ? invoicesPopupFilteredInvoices : [],
          [invoicesSortConfig.key || 'orderNumber'],
          [invoicesSortConfig.direction || 'asc'],
        ).slice(0, 20),
      ],
      'orderNumber',
    ),
    [
      invoicesPopupFilteredInvoices,
      selectedInvoices,
      invoicesSortConfig,
    ],
  )

  return {
    // == BankReconciliationPageView
    isSidebarOpen,
    selectedBankTransfer,
    setsidebarOpen,
    // == PrimaryView
    onBankTransfersAmountFilterChange,
    currentPage,
    ...calculateSummary(bankTransfers),
    onBankTransfersDateFilterChange,
    onItemsPerPageChange,
    onRowClick,
    onTransactionSearchPress,
    currentPageBankTransfers,
    setcurrentPage,
    setfilterStatus,
    transactionSearchTerm,
    settransactionSearchTerm,
    statusFilter,
    totalPages,
    onPageChange,
    handleDateFilterChange,
    ...backendActionsLogic,
    // == TransactionDetailsSidebar
    onSidebarClose,
    onSideBarMainScreenPress,
    sideBarReconcileButtonColor,
    sideBarReconcileButtonText,
    onSideBarNextPress,
    onSideBarPrevPress,
    onSideBarPrevPagePress,
    onSideBarNextPagePress,
    // onBankTransfersDateFilterChange,
    onInvoicesAmountFilterChange,
    isReconciledSuccessMessageVisible,
    onReconcileSuccessMessageClose,
    // == InvoicesPopup
    matchedInvoices,
    // onInvoicesAmountFilterChange,
    onInvoicesDateFilterChange,
    // selectedBankTransfer,
    isInvoicesPopupModalOpen,
    isInvoicesPopupMatchesValidatedSideMessageVisible,
    showInvoicesPopupMessageDeleted,
    invoicesPopupFilteredInvoices: sortedInvoices,
    onMatchSelectedInvoicesPressErrorMessage,
    onInvoiceSearchPress,
    onInvoiceCheckboxPress,
    setisInvoicesPopupModalOpen, // TODO : refacto to onXXOpen
    selectedInvoices,
    onInvoiceUnmatchPressErrorMessage,
    //
    // == FilterStatusButton
    onTransactionTabPress,
    // == Comment
    commentErrorMessage,
    // == PaginationComponent
    itemsPerPage,
    // == CommentFilter
    isCommentFilterActive,
    onCommentFilterButtonPress,
    onCommentFilterFocusKeyPress,
    // == PlusFilter
    plusFilterShowMore,
    onPlusFilterPlusClick,
    onPlusFilterLessClick,
    // == VerificationSelector
    isVerificationSelectorFilterOpen,
    onVerificationSelectorClearFilter,
    onVerificationSelectorClose,
    onVerificationSelectorSelect,
    verificationSelectorSelectedFilter,
    verificationSelectorToggleFilter,
    // == TransactionsTable
    // currentPageBankTransfers,
    getBankTransferRowBgColor,
    // == UnlinkedInvoicesModal
    isUnlinkedInvoicesModalOpen,
    onUnlinkedInvoicesPress,
    onUnlinkedInvoicesModalClose,
    unlinkedInvoices: sortedUnlinkedInvoices,
    unlinkedInvoicesSortConfig,
    onUnlinkedInvoicesSort,
    onUnlinkedInvoicesSearchPress,
    // Smart Invoice Selector
    smartInvoiceSelectorPrompt,
    smartInvoiceSelectorFiles,
    isSmartInvoiceSelectorProcessing,
    smartInvoiceSelectorError,
    onSmartInvoiceSelectorPromptChange,
    onSmartInvoiceSelectorFilesChange,
    onSmartInvoiceSelectorSubmit,
    invoicesSortConfig,
    onInvoicesSort,
  }
}

export default useBankReconciliationPage
